import React, { useState, useMemo, useCallback } from "react";
import TelegramNotificationSettings from "./TelegramNotificationSettings";
import {
  SPORTSBOOK_GROUPS,
  SELECTABLE_SPORTSBOOK_OPTIONS,
  SELECTABLE_SPORTSBOOK_IDS,
  normalizeSportsbookId,
  isSelectableSportsbookId,
} from "./sportsbookCatalog.shared";

// ── Colour tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "#050816",
  card: "#0d1a38",
  cardBorder: "rgba(180,197,235,0.10)",
  cardHover: "#111f42",
  accent: "#4c6fff",
  accentSoft: "rgba(76,111,255,0.14)",
  text: "#f4f7ff",
  muted: "#a8b3cf",
  dimmer: "#7d89a8",
  positive: "#00c878",
  positiveBg: "rgba(0,200,120,0.10)",
  positiveBorder: "rgba(0,200,120,0.22)",
  neutral: "#f2d878",
  neutralBg: "rgba(242,216,120,0.10)",
  neutralBorder: "rgba(242,216,120,0.22)",
  negative: "#ff6b4a",
  negativeBg: "rgba(255,107,74,0.10)",
  negativeBorder: "rgba(255,107,74,0.22)",
  inputBg: "#091022",
  inputBorder: "rgba(180,197,235,0.15)",
  divider: "rgba(180,197,235,0.08)",
};

// ── Options persistence keys ──────────────────────────────────────────────────
const OPTIONS_ODDS_FORMAT_KEY = "analysis_options_odds_format";
const OPTIONS_SPORTSBOOKS_KEY = "analysis_options_enabled_sportsbooks";

function normalizeOptionsOddsFormat(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "hk" || v === "decimal" || v === "american") return v;
  return "hk";
}

function getInitialOptionsOddsFormat() {
  if (typeof window === "undefined") return "hk";
  try {
    return normalizeOptionsOddsFormat(localStorage.getItem(OPTIONS_ODDS_FORMAT_KEY));
  } catch {
    return "hk";
  }
}

function saveOptionsOddsFormat(format) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(OPTIONS_ODDS_FORMAT_KEY, normalizeOptionsOddsFormat(format));
    window.dispatchEvent(new CustomEvent("analysis_odds_format_changed"));
  } catch {}
}

// ── Sportsbook options list ───────────────────────────────────────────────────
// PHASE 2D.1: SPORTSBOOK_GROUPS / normalizeSportsbookId (and the
// selectable-id helpers) now come from the shared provisional catalog
// ("./sportsbookCatalog.shared"), imported above. Do not re-declare or
// copy the 48-item list here.

// ── Sportsbook persistence helpers ────────────────────────────────────────────
function getInitialEnabledSportsbooks() {
  const fallback = () => [...SELECTABLE_SPORTSBOOK_IDS];
  if (typeof window === "undefined") return fallback();
  try {
    const raw = localStorage.getItem(OPTIONS_SPORTSBOOKS_KEY);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback();
    const normalized = [
      ...new Set(
        parsed
          .map(normalizeSportsbookId)
          .filter((id) => isSelectableSportsbookId(id))
      ),
    ];
    return normalized.length > 0 ? normalized : fallback();
  } catch {
    return fallback();
  }
}

function saveEnabledSportsbooks(ids) {
  const normalizedIds = [
    ...new Set(
      (ids || [])
        .map(normalizeSportsbookId)
        .filter((id) => isSelectableSportsbookId(id))
    ),
  ];
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(OPTIONS_SPORTSBOOKS_KEY, JSON.stringify(normalizedIds));
      window.dispatchEvent(new CustomEvent("analysis_sportsbooks_changed", { detail: { enabledSportsbooks: normalizedIds } }));
    } catch {}
  }
  return normalizedIds;
}

// ── Display conversion helpers ────────────────────────────────────────────────
function decimalToAmerican(decimal) {
  const n = Number(decimal);
  if (!Number.isFinite(n) || n <= 1) return null;
  if (n >= 2) return Math.round((n - 1) * 100);
  return Math.round(-100 / (n - 1));
}

function formatOddsByOptionsFormatFromDecimal(decimal, format) {
  const n = Number(decimal);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const f = normalizeOptionsOddsFormat(format);

  if (f === "decimal") {
    return n.toFixed(3).replace(/(?:\.0+|(\.\d*?)0+)$/, "$1") || n.toFixed(3);
  }

  if (f === "american") {
    if (n <= 1) return "—";
    const american = decimalToAmerican(n);
    if (!Number.isFinite(american)) return "—";
    return american > 0 ? `+${american}` : String(american);
  }

  // HK
  const hk = n - 1;
  return hk.toFixed(3).replace(/(?:\.0+|(\.\d*?)0+)$/, "$1") || hk.toFixed(3);
}

function getOptionsOddsFormatLabel(format) {
  const f = normalizeOptionsOddsFormat(format);
  if (f === "decimal") return "Decimal";
  if (f === "american") return "American";
  return "HK";
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function parseHK(v) {
  const n = parseFloat(String(v).trim());
  return isNaN(n) || n <= 0 ? null : n;
}

// Accepts negative HK > -1 (e.g. -0.95 → decimal 0.05), rejects <= -1 and 0
function parseCalculatorHK(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n <= -1 || n === 0) return null;
  return n;
}

// ── Odds input normalization helpers ─────────────────────────────────────────
function stripSpaces(value) {
  return String(value ?? "").trim().replace(/\s+/g, "");
}

function hasDecimalPoint(value) {
  return String(value ?? "").includes(".");
}

function normalizeCompactHKInput(raw) {
  const s = stripSpaces(raw);
  if (!s) return "";
  if (hasDecimalPoint(s)) return s;
  const sign = s.startsWith("-") ? "-" : "";
  const body = sign ? s.slice(1) : s;
  if (!/^\d+$/.test(body)) return s;
  if (/^0\d+$/.test(body)) {
    return `${sign}0.${body.slice(1)}`;
  }
  if (body.length === 3 || body.length === 4) {
    return `${sign}${body.slice(0, 1)}.${body.slice(1)}`;
  }
  return `${sign}${body}`;
}

function normalizeCompactDecimalInput(raw) {
  const s = stripSpaces(raw);
  if (!s) return "";
  if (hasDecimalPoint(s)) return s;
  const sign = s.startsWith("-") ? "-" : "";
  const body = sign ? s.slice(1) : s;
  if (!/^\d+$/.test(body)) return s;
  if (body.length <= 2) return `${sign}${body}`;
  const whole = body.slice(0, -2) || "0";
  const frac = body.slice(-2);
  return `${sign}${Number(whole)}.${frac}`;
}

function normalizeCompactAmericanInput(raw) {
  const s = stripSpaces(raw);
  if (!s) return "";
  if (s.startsWith("+") || s.startsWith("-")) return s;
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n > 0 ? `+${s}` : s;
}

function normalizeOddsInputOnBlur(raw, format) {
  const f = normalizeOptionsOddsFormat(format);
  if (f === "american") return normalizeCompactAmericanInput(raw);
  if (f === "decimal") return normalizeCompactDecimalInput(raw);
  return normalizeCompactHKInput(raw);
}

function parseOddsInputToDecimal(value, format) {
  const f = normalizeOptionsOddsFormat(format);
  const normalized = normalizeOddsInputOnBlur(value, f);
  if (!normalized) return null;
  if (f === "american") {
    const n = Number(String(normalized).replace(/^\+/, ""));
    if (!Number.isFinite(n) || n === 0) return null;
    if (n > 0) return 1 + n / 100;
    return 1 + 100 / Math.abs(n);
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  if (f === "decimal") {
    if (n <= 1) return null;
    return n;
  }
  // HK
  if (n <= -1 || n === 0) return null;
  const decimal = n + 1;
  if (decimal <= 0) return null;
  return decimal;
}

function getOddsInputPlaceholder(format, type) {
  const f = normalizeOptionsOddsFormat(format);
  if (f === "american") return "ví dụ: +150 hoặc 150";
  if (f === "decimal") return "ví dụ: 2.06 hoặc 206";
  if (type === "small") return "ví dụ: 0.334 hoặc 0334";
  if (type === "fair") return "ví dụ: 0.75 hoặc 075";
  if (type === "pinnacle") return "ví dụ: 2.110 hoặc 2110";
  return "ví dụ: 1.06 hoặc 106";
}

function getOddsInputErrorMessage(format) {
  const f = normalizeOptionsOddsFormat(format);
  if (f === "decimal") {
    return "Decimal odds không hợp lệ. Decimal phải lớn hơn 1. Có thể nhập nhanh 206 để thành 2.06.";
  }
  if (f === "american") {
    return "American odds không hợp lệ. Nhập +150, 150 hoặc -150.";
  }
  return "HK odds không hợp lệ. Nhập số lớn hơn -1, khác 0. Có thể nhập nhanh 106 để thành 1.06 hoặc 0334 để thành 0.334.";
}

function fmtNum(n, dp = 2) {
  return n.toFixed(dp);
}

function fmtSignedPct(n) {
  if (!Number.isFinite(n)) return "—";
  const fixed = n.toFixed(2);
  return n > 0 ? `+${fixed}%` : `${fixed}%`;
}

function evStatus(ev) {
  if (ev > 0.05) return { label: "Có value", color: C.positive, bg: C.positiveBg, border: C.positiveBorder };
  if (Math.abs(ev) <= 0.05) return { label: "Hoà vốn", color: C.neutral, bg: C.neutralBg, border: C.neutralBorder };
  return { label: "Không value", color: C.negative, bg: C.negativeBg, border: C.negativeBorder };
}

// ── Shared UI pieces ──────────────────────────────────────────────────────────
function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: C.dimmer, marginBottom: 4, letterSpacing: "0.4px", textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, onBlur, placeholder }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      style={{
        width: "100%",
        background: C.inputBg,
        border: `1px solid ${C.inputBorder}`,
        borderRadius: 8,
        padding: "9px 12px",
        color: C.text,
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

function Field({ label, value, onChange, onBlur, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Label>{label}</Label>
      <NumberInput value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder} />
    </div>
  );
}

function ResultPill({ label, value, color, bg, border }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      padding: "8px 12px", marginBottom: 6,
    }}>
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center",
      background: status.bg, border: `1px solid ${status.border}`,
      borderRadius: 20, padding: "4px 12px",
      fontSize: 12, fontWeight: 700, color: status.color,
      marginTop: 4,
    }}>
      {status.label}
    </div>
  );
}

function SectionDivider() {
  return <div style={{ height: 1, background: C.divider, margin: "16px 0" }} />;
}

function Note({ children }) {
  return (
    <div style={{
      fontSize: 11, color: C.dimmer, background: "rgba(76,111,255,0.06)",
      border: `1px solid ${C.accentSoft}`, borderRadius: 8,
      padding: "8px 10px", marginTop: 12, lineHeight: 1.55,
    }}>
      {children}
    </div>
  );
}

function ModeTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: "7px 4px", fontSize: 12, fontWeight: 600,
        background: active ? C.accent : "transparent",
        color: active ? "#fff" : C.muted,
        border: `1px solid ${active ? C.accent : C.inputBorder}`,
        borderRadius: 7, cursor: "pointer", transition: "all .15s",
      }}
    >
      {label}
    </button>
  );
}

// ── Part 5: Value Bet Calculator ──────────────────────────────────────────────
function ValueBetCalc({ optionsOddsFormat }) {
  const [mode, setMode] = useState("quick");

  // Mode A – Tính nhanh
  const [offerHK, setOfferHK] = useState("");
  const [fairHK, setFairHK] = useState("");

  // Mode B – Pinnacle 2 chiều
  const [valueSabaHK, setValueSabaHK] = useState("");
  const [pinSameHK, setPinSameHK] = useState("");
  const [pinOppHK, setPinOppHK] = useState("");

  const quickResult = useMemo(() => {
    if (offerHK.trim() === "" && fairHK.trim() === "") return null;
    const offerDec = parseOddsInputToDecimal(offerHK, optionsOddsFormat);
    const fairDec = parseOddsInputToDecimal(fairHK, optionsOddsFormat);
    if (offerDec === null || fairDec === null) return { type: "invalid" };
    const ev = ((offerDec / fairDec) - 1) * 100;
    return { type: "ok", ev, offerDec, fairDec };
  }, [offerHK, fairHK, optionsOddsFormat]);

  const pinResult = useMemo(() => {
    if (valueSabaHK.trim() === "" && pinSameHK.trim() === "" && pinOppHK.trim() === "") return null;
    const sabaDecimal = parseOddsInputToDecimal(valueSabaHK, optionsOddsFormat);
    const pinSameDecimal = parseOddsInputToDecimal(pinSameHK, optionsOddsFormat);
    const pinOppDecimal = parseOddsInputToDecimal(pinOppHK, optionsOddsFormat);
    if (sabaDecimal === null || pinSameDecimal === null || pinOppDecimal === null) return { type: "invalid" };
    const rawProbSame = 1 / pinSameDecimal;
    const rawProbOpp = 1 / pinOppDecimal;
    const totalRawProb = rawProbSame + rawProbOpp;
    const fairProbSame = rawProbSame / totalRawProb;
    const fairDecimalSame = 1 / fairProbSame;
    const fairHKSame = fairDecimalSame - 1;
    const ev = (sabaDecimal * fairProbSame - 1) * 100;
    return {
      type: "ok",
      sabaDecimal, pinSameDecimal, pinOppDecimal,
      rawProbSame, rawProbOpp, totalRawProb,
      fairProbSame, fairDecimalSame, fairHKSame,
      ev,
    };
  }, [valueSabaHK, pinSameHK, pinOppHK, optionsOddsFormat]);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <ModeTab label="Tính nhanh" active={mode === "quick"} onClick={() => setMode("quick")} />
        <ModeTab label="Pinnacle 2 chiều" active={mode === "pin"} onClick={() => setMode("pin")} />
      </div>

      {mode === "quick" && (
        <div>
          <Field
            label={`Odds nhà cái muốn cược (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
            value={offerHK}
            onChange={setOfferHK}
            onBlur={() => setOfferHK(normalizeOddsInputOnBlur(offerHK, optionsOddsFormat))}
            placeholder={getOddsInputPlaceholder(optionsOddsFormat, "offer")}
          />
          <Field
            label={`Fair odds đã khử margin (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
            value={fairHK}
            onChange={setFairHK}
            onBlur={() => setFairHK(normalizeOddsInputOnBlur(fairHK, optionsOddsFormat))}
            placeholder={getOddsInputPlaceholder(optionsOddsFormat, "fair")}
          />

          {quickResult === null && <Note>Nhập odds để tính</Note>}
          {quickResult?.type === "invalid" && (
            <div style={{ color: C.negative, fontSize: 13, marginTop: 8 }}>
              {getOddsInputErrorMessage(optionsOddsFormat)}
            </div>
          )}
          {quickResult?.type === "ok" && (() => {
            const st = evStatus(quickResult.ev);
            return (
              <div>
                <SectionDivider />
                <div style={{ fontSize: 11, color: C.dimmer, marginBottom: 8 }}>
                  Odds cược ({getOptionsOddsFormatLabel(optionsOddsFormat)}): <strong style={{ color: C.muted }}>{formatOddsByOptionsFormatFromDecimal(quickResult.offerDec, optionsOddsFormat)}</strong>
                  {"  ·  "}Fair odds ({getOptionsOddsFormatLabel(optionsOddsFormat)}): <strong style={{ color: C.muted }}>{formatOddsByOptionsFormatFromDecimal(quickResult.fairDec, optionsOddsFormat)}</strong>
                </div>
                <ResultPill label="EV %" value={fmtSignedPct(quickResult.ev)} color={st.color} bg={st.bg} border={st.border} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                  <StatusBadge status={st} />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {mode === "pin" && (
        <div>
          <Field
            label={`Saba odds cửa muốn cược (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
            value={valueSabaHK}
            onChange={setValueSabaHK}
            onBlur={() => setValueSabaHK(normalizeOddsInputOnBlur(valueSabaHK, optionsOddsFormat))}
            placeholder={getOddsInputPlaceholder(optionsOddsFormat, "offer")}
          />
          <Field
            label={`Pinnacle odds cùng cửa (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
            value={pinSameHK}
            onChange={setPinSameHK}
            onBlur={() => setPinSameHK(normalizeOddsInputOnBlur(pinSameHK, optionsOddsFormat))}
            placeholder={getOddsInputPlaceholder(optionsOddsFormat, "pinnacle")}
          />
          <Field
            label={`Pinnacle odds cửa đối diện cùng line (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
            value={pinOppHK}
            onChange={setPinOppHK}
            onBlur={() => setPinOppHK(normalizeOddsInputOnBlur(pinOppHK, optionsOddsFormat))}
            placeholder={getOddsInputPlaceholder(optionsOddsFormat, "small")}
          />

          {pinResult === null && <Note>Nhập đủ 3 odds để tính</Note>}
          {pinResult?.type === "invalid" && (
            <div style={{ color: C.negative, fontSize: 13, marginTop: 8 }}>
              {getOddsInputErrorMessage(optionsOddsFormat)}
            </div>
          )}
          {pinResult?.type === "ok" && (() => {
            const st = evStatus(pinResult.ev);
            return (
              <div>
                <SectionDivider />
                <ResultPill label={`Saba odds (${getOptionsOddsFormatLabel(optionsOddsFormat)})`} value={formatOddsByOptionsFormatFromDecimal(pinResult.sabaDecimal, optionsOddsFormat)} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <ResultPill label={`Pinnacle cùng cửa (${getOptionsOddsFormatLabel(optionsOddsFormat)})`} value={formatOddsByOptionsFormatFromDecimal(pinResult.pinSameDecimal, optionsOddsFormat)} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <ResultPill label={`Pinnacle đối diện (${getOptionsOddsFormatLabel(optionsOddsFormat)})`} value={formatOddsByOptionsFormatFromDecimal(pinResult.pinOppDecimal, optionsOddsFormat)} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <SectionDivider />
                <ResultPill label="Raw prob cùng cửa" value={`${fmtNum(pinResult.rawProbSame * 100, 2)}%`} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <ResultPill label="Raw prob đối diện" value={`${fmtNum(pinResult.rawProbOpp * 100, 2)}%`} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <ResultPill label="Tổng raw prob" value={`${fmtNum(pinResult.totalRawProb * 100, 2)}%`} color={C.dimmer} bg="rgba(255,255,255,0.04)" border={C.divider} />
                <SectionDivider />
                <ResultPill label="Fair prob cùng cửa" value={`${fmtNum(pinResult.fairProbSame * 100, 2)}%`} color={C.accent} bg={C.accentSoft} border="rgba(76,111,255,0.25)" />
                <ResultPill label={`Fair odds đã khử margin (${getOptionsOddsFormatLabel(optionsOddsFormat)})`} value={formatOddsByOptionsFormatFromDecimal(pinResult.fairDecimalSame, optionsOddsFormat)} color={C.accent} bg={C.accentSoft} border="rgba(76,111,255,0.25)" />
                <SectionDivider />
                <ResultPill label="EV %" value={fmtSignedPct(pinResult.ev)} color={st.color} bg={st.bg} border={st.border} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                  <StatusBadge status={st} />
                </div>
              </div>
            );
          })()}

          <Note>Nhập đúng 3 odds cùng trận, cùng market, cùng FT/map/set, cùng line. Pinnacle odds cùng cửa và cửa đối diện phải là 2 phía đối nghịch của cùng một line.</Note>
        </div>
      )}
    </div>
  );
}

// ── Part 6: Arbitrage Calculator ──────────────────────────────────────────────
function ArbCalc({ optionsOddsFormat }) {
  const [bankroll, setBankroll] = useState("");
  const [hk1, setHk1] = useState("");
  const [hk2, setHk2] = useState("");

  const result = useMemo(() => {
    if (bankroll.trim() === "" && hk1.trim() === "" && hk2.trim() === "") return null;
    const bk = parseFloat(bankroll);
    const dec1 = parseOddsInputToDecimal(hk1, optionsOddsFormat);
    const dec2 = parseOddsInputToDecimal(hk2, optionsOddsFormat);
    if (isNaN(bk) || bk <= 0 || dec1 === null || dec2 === null) return { type: "invalid" };
    const implied = 1 / dec1 + 1 / dec2;
    const arbPct = (1 / implied - 1) * 100;
    const stake1 = bk * (1 / dec1) / implied;
    const stake2 = bk * (1 / dec2) / implied;
    const payout1 = stake1 * dec1;
    const payout2 = stake2 * dec2;
    const profit = Math.min(payout1, payout2) - bk;
    return { type: "ok", implied, arbPct, stake1, stake2, payout1, payout2, profit };
  }, [bankroll, hk1, hk2, optionsOddsFormat]);

  const hasArb = result?.type === "ok" && result.implied < 1;

  return (
    <div>
      <Field label="Tổng vốn" value={bankroll} onChange={setBankroll} placeholder="ví dụ: 1000" />
      <Field
        label={`Odds cửa 1 (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
        value={hk1}
        onChange={setHk1}
        onBlur={() => setHk1(normalizeOddsInputOnBlur(hk1, optionsOddsFormat))}
        placeholder={getOddsInputPlaceholder(optionsOddsFormat, "offer")}
      />
      <Field
        label={`Odds cửa 2 (${getOptionsOddsFormatLabel(optionsOddsFormat)})`}
        value={hk2}
        onChange={setHk2}
        onBlur={() => setHk2(normalizeOddsInputOnBlur(hk2, optionsOddsFormat))}
        placeholder={getOddsInputPlaceholder(optionsOddsFormat, "offer")}
      />

      {result === null && <Note>Nhập vốn và odds để tính</Note>}
      {result?.type === "invalid" && (
        <div style={{ color: C.negative, fontSize: 13, marginTop: 8 }}>
          Vốn phải &gt; 0. {getOddsInputErrorMessage(optionsOddsFormat)}
        </div>
      )}
      {result?.type === "ok" && (
        <div>
          <SectionDivider />
          <div style={{
            display: "inline-flex", alignItems: "center",
            background: hasArb ? C.positiveBg : C.negativeBg,
            border: `1px solid ${hasArb ? C.positiveBorder : C.negativeBorder}`,
            borderRadius: 20, padding: "4px 14px",
            fontSize: 13, fontWeight: 700, color: hasArb ? C.positive : C.negative,
            marginBottom: 10,
          }}>
            {hasArb ? "✓ Có arb" : "✗ Không có arb"}
          </div>

          <ResultPill
            label="Arb %"
            value={`${fmtNum(result.arbPct)}%`}
            color={hasArb ? C.positive : C.negative}
            bg={hasArb ? C.positiveBg : C.negativeBg}
            border={hasArb ? C.positiveBorder : C.negativeBorder}
          />
          <SectionDivider />
          <ResultPill label="Tiền cược cửa 1" value={fmtNum(result.stake1)} color={C.accent} bg={C.accentSoft} border="rgba(76,111,255,0.20)" />
          <ResultPill label="Tiền cược cửa 2" value={fmtNum(result.stake2)} color={C.accent} bg={C.accentSoft} border="rgba(76,111,255,0.20)" />
          <SectionDivider />
          <ResultPill label="Payout cửa 1" value={fmtNum(result.payout1)} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
          <ResultPill label="Payout cửa 2" value={fmtNum(result.payout2)} color={C.muted} bg="rgba(255,255,255,0.04)" border={C.divider} />
          <SectionDivider />
          <ResultPill
            label="Lợi nhuận chắc chắn"
            value={fmtNum(result.profit)}
            color={result.profit >= 0 ? C.positive : C.negative}
            bg={result.profit >= 0 ? C.positiveBg : C.negativeBg}
            border={result.profit >= 0 ? C.positiveBorder : C.negativeBorder}
          />
        </div>
      )}
    </div>
  );
}

// ── Part 7: Odds Display Settings ────────────────────────────────────────────
function OddsSettings({ oddsFormat, onOddsFormatChange }) {
  const OPTIONS = [
    { value: "hk",       label: "HK" },
    { value: "decimal",  label: "Decimal" },
    { value: "american", label: "American" },
  ];

  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.55 }}>
        Chọn định dạng odds muốn hiển thị trong máy tính.
      </div>
      <Label>Định dạng odds</Label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onOddsFormatChange(opt.value)}
            style={{
              padding: "8px 18px", fontSize: 13, fontWeight: 600,
              background: oddsFormat === opt.value ? C.accent : "transparent",
              color: oddsFormat === opt.value ? "#fff" : C.muted,
              border: `1px solid ${oddsFormat === opt.value ? C.accent : C.inputBorder}`,
              borderRadius: 8, cursor: "pointer", transition: "all .15s",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
        Đang hiển thị: <strong style={{ color: C.text }}>{getOptionsOddsFormatLabel(oddsFormat)}</strong>
      </div>
      <Note>Đã lưu lựa chọn cho máy tính trong Tuỳ Chọn. Chưa áp dụng sang các tab dữ liệu thật.</Note>
    </div>
  );
}

// ── Part 8: Threshold Settings ────────────────────────────────────────────────
function ThresholdSettings() {
  const [evMin, setEvMin] = useState("5");
  const [arbMin, setArbMin] = useState("0.5");
  const [middlesMin, setMiddlesMin] = useState("0");

  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.55 }}>
        Đặt ngưỡng lọc tối thiểu cho từng loại cơ hội.
      </div>
      <Field label="EV tối thiểu (%)" value={evMin} onChange={setEvMin} placeholder="5" />
      <Field label="Arb tối thiểu (%)" value={arbMin} onChange={setArbMin} placeholder="0.5" />
      <Field label="Middles tối thiểu (%)" value={middlesMin} onChange={setMiddlesMin} placeholder="0" />
      <Note>Chỉ là bản nháp UI, chưa ảnh hưởng dữ liệu thật.</Note>
    </div>
  );
}

// ── Guide helper components ───────────────────────────────────────────────────
function GuideSection({ icon, title, children, tone }) {
  const toneMap = {
    value:   { accent: C.positive,  bg: C.positiveBg,  border: C.positiveBorder },
    arb:     { accent: C.accent,    bg: C.accentSoft,  border: "rgba(76,111,255,0.22)" },
    middles: { accent: C.neutral,   bg: C.neutralBg,   border: C.neutralBorder },
    warning: { accent: C.negative,  bg: C.negativeBg,  border: C.negativeBorder },
  };
  const t = toneMap[tone] || toneMap.arb;
  return (
    <div style={{
      background: "transparent",
      border: "none",
      borderRadius: 0,
      padding: "0 0 14px",
      marginBottom: 16,
      borderBottom: `1px solid ${C.divider}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 7,
        fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 8,
      }}>
        <span style={{
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${C.divider}`,
          borderRadius: 8, padding: "2px 6px", fontSize: 12, color: t.accent,
        }}>{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

function GuideText({ children }) {
  return (
    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.65, marginBottom: 10, marginTop: 0 }}>
      {children}
    </p>
  );
}

function GuideList({ items, type }) {
  return (
    <div style={{ marginBottom: 4 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          display: "flex", gap: 8,
          background: "rgba(255,255,255,0.025)",
          border: "none",
          borderRadius: 8,
          padding: "7px 8px",
          marginBottom: 5,
          fontSize: 12, color: C.muted, lineHeight: 1.45,
        }}>
          <span style={{ flexShrink: 0, fontWeight: 800, color: C.accent, minWidth: 18 }}>
            {type === "check" ? "✓" : `${i + 1}.`}
          </span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

function GuideSubTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, color: C.dimmer, marginBottom: 7, marginTop: 12, letterSpacing: "0.4px", textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

function UserGuide() {
  return (
    <div style={{ paddingBottom: 4 }}>
      <GuideSection icon="💰" title="Value Bet" tone="value">
        <GuideText>
          Tab Value hiển thị các cơ hội có EV dương theo dữ liệu phân tích. Tuy nhiên, cơ hội trên giao diện chỉ nên xem là tín hiệu ban đầu. Trước khi đặt cược, user cần mở trực tiếp nhà cái để kiểm tra lại đúng trận, đúng tên cược, đúng market, đúng line và đúng odds hiện tại.
        </GuideText>
        <GuideSubTitle>Cách dùng khuyến nghị</GuideSubTitle>
        <GuideList type="numbered" items={[
          "Mở đúng nhà cái muốn cược đang hiển thị trên kèo.",
          "Kiểm tra kỹ: đúng trận, đúng đội/vận động viên, đúng cửa cược, đúng market, đúng line và đúng odds thực tế.",
          "Nếu odds hoặc line đã thay đổi, không nên cược theo số cũ trên giao diện.",
          "Nếu thông tin vẫn khớp, hãy nhập vào Máy tính Value: odds thực tế ở nhà cái bạn muốn cược, odds cùng cửa/cùng market/cùng line ở nhà cái nguồn chuẩn, và odds cửa đối diện cùng market/cùng line để máy tính xác suất.",
          "Nếu kết quả là EV dương, có thể cân nhắc cược. Nếu EV âm, không nên vào cược vì kèo không còn lợi thế.",
        ]} />
        <GuideText>
          Lưu ý: Odds live thay đổi rất nhanh. Luôn kiểm tra trực tiếp tại nhà cái và tính lại EV trước khi đặt cược.
        </GuideText>
      </GuideSection>

      <GuideSection icon="⚖️" title="Arbitrage" tone="arb">
        <GuideText>
          Tab Arb dùng để tìm cơ hội surebet/arbitrage giữa hai nhà cái. Nếu đặt đúng tỷ lệ vốn ở hai cửa đối nghịch, user có thể khóa lợi nhuận theo mức profit hiển thị. Tuy nhiên, Arb yêu cầu kiểm tra cực kỹ vì chỉ cần sai line, sai market hoặc một bên odds thay đổi thì cơ hội có thể không còn đúng.
        </GuideText>
        <GuideSubTitle>Cách kiểm tra Arb</GuideSubTitle>
        <GuideList type="numbered" items={[
          "Mở cả hai nhà cái đang hiển thị.",
          "Kiểm tra đúng trận, đúng market, đúng line và đúng hai cửa đối nghịch.",
          "Đảm bảo odds ở cả hai nhà cái vẫn còn khớp.",
          "Nếu một bên đã đổi odds hoặc line, cần tính lại trước khi cược.",
          "Chỉ vào cược khi cả hai cửa vẫn đúng và profit còn đủ tốt.",
        ]} />
        <GuideSubTitle>Kèo Arb tốt nên có</GuideSubTitle>
        <GuideList type="check" items={[
          "Profit đủ cao để bù trượt odds hoặc phí.",
          "Hai book đều là nhà cái user có thể đặt được.",
          "Tên cược, market và line rõ ràng.",
          "Không nhầm FT với hiệp, set hoặc map.",
          "Không chỉ nhìn % cao mà bỏ qua kiểm tra thực tế.",
        ]} />
      </GuideSection>

      <GuideSection icon="🎯" title="Middles" tone="middles">
        <GuideText>
          Tab Middles dùng để tìm các cơ hội có khoảng trống giữa hai line. Middles không đảm bảo lợi nhuận chắc chắn như Arb, nhưng nếu line lệch đẹp, user có thể có cơ hội thắng cả hai đầu.
        </GuideText>
        <GuideSubTitle>Cách kiểm tra Middles</GuideSubTitle>
        <GuideList type="numbered" items={[
          "Mở đúng hai nhà cái đang hiển thị.",
          "Kiểm tra đúng trận, đúng market, đúng cửa cược và đúng line của từng bên.",
          "Đảm bảo line tạo ra khoảng giữa thật sự.",
          "Kiểm tra odds hai cửa còn đủ tốt.",
          "Không nên cược nếu khoảng giữa quá hẹp hoặc odds hai cửa quá thấp.",
        ]} />
        <GuideSubTitle>Kèo Middles tốt nên có</GuideSubTitle>
        <GuideList type="check" items={[
          "Khoảng trống giữa hai line càng rộng càng tốt.",
          "Odds hai cửa càng cao càng tốt.",
          "Mức lỗ nếu không rơi vào khoảng giữa càng thấp càng tốt.",
          "Market rõ ràng, không nhầm FT/hiệp/set/map.",
          "Hai nhà cái đều có thể đặt được.",
        ]} />
      </GuideSection>

      <GuideSection icon="⚠️" title="Lưu ý chung" tone="warning">
        <GuideText>
          Dữ liệu trên giao diện chỉ là tín hiệu phân tích cơ hội, không đảm bảo lợi nhuận. Odds thể thao thay đổi rất nhanh, đặc biệt ở kèo live. Trước khi đặt cược, user luôn cần kiểm tra lại trực tiếp tại nhà cái: đúng trận, đúng market, đúng tên cược, đúng line và đúng odds.
        </GuideText>
        <GuideText>
          Không nên đặt cược chỉ vì thấy EV hoặc profit cao trên giao diện. Chỉ nên vào cược khi odds thực tế tại nhà cái vẫn khớp và đã kiểm tra lại bằng công cụ phù hợp.
        </GuideText>
      </GuideSection>
    </div>
  );
}

function AboutUs() {
  return (
    <div style={{ paddingBottom: 4 }}>
      <GuideSection icon="👥" title="Giới thiệu về chúng tôi" tone="arb">
        <GuideText>Chúng tôi xây dựng phần mềm này để giúp người dùng tìm kiếm cơ hội cược thể thao dựa trên dữ liệu, toán học và xác suất — không phải dựa vào cảm tính hay may rủi.</GuideText>
        <GuideText>Trong thực tế, nếu một tài khoản cá nhân thắng quá nhiều hoặc khai thác được các cơ hội tốt trong thời gian dài, nhà cái có thể giới hạn mức cược, yêu cầu xác minh thêm hoặc làm giảm khả năng đặt cược hiệu quả. Vì vậy, thay vì chỉ sử dụng công cụ cho riêng mình, chúng tôi muốn chia sẻ hệ thống này để cùng cộng đồng tối ưu hóa lợi nhuận và hợp tác chiến thắng nhà cái một cách thông minh hơn.</GuideText>
        <GuideText>Các tính năng như Value, Arbitrage, Middles và Steam đều được xây dựng dựa trên dữ liệu odds, công thức xác suất và chênh lệch thị trường. Mục tiêu là giúp người dùng phát hiện những điểm mà odds có thể đang tạo ra lợi thế.</GuideText>
        <GuideText>Chúng tôi luôn mong muốn người dùng thắng nhà cái bằng cách tiếp cận có kỷ luật: chọn kèo tốt, kiểm tra odds thực tế, quản lý vốn hợp lý và không đặt cược theo cảm xúc.</GuideText>
        <GuideText>Phần mềm không đảm bảo lợi nhuận tuyệt đối. Odds có thể thay đổi rất nhanh, đặc biệt ở kèo live. Trước khi đặt cược, bạn luôn nên kiểm tra lại trực tiếp tại nhà cái và sử dụng công cụ tính toán phù hợp.</GuideText>
        <GuideText>Cá cược có lợi thế không nằm ở may mắn, mà nằm ở dữ liệu tốt, tốc độ, kỷ luật và cách sử dụng xác suất đúng đắn.</GuideText>
        <GuideText>Cảm ơn bạn đã đồng hành cùng chúng tôi.</GuideText>
      </GuideSection>
    </div>
  );
}

function ImportantNotice() {
  return (
    <div style={{ paddingBottom: 4 }}>
      <GuideSection icon="⚠️" title="Lưu ý quan trọng khi sử dụng dữ liệu odds" tone="warning">
        <GuideText>
          Phần mềm của chúng tôi tổng hợp và phân tích dữ liệu odds từ nhiều nhà cái khác nhau để hiển thị các tín hiệu cơ hội cho người dùng. Tuy nhiên, do lượng dữ liệu lớn và tỷ lệ cược thay đổi liên tục theo thời gian thực, một số cơ hội hiển thị trên giao diện có thể không còn khớp hoàn toàn với thông tin tại nhà cái ở thời điểm bạn mở kèo.
        </GuideText>

        <GuideSubTitle>Một số lý do có thể gây ra chênh lệch</GuideSubTitle>
        <GuideList type="check" items={[
          "Tỷ lệ cược hoặc line đã thay đổi trước khi bạn vào nhà cái.",
          "Nhà cái đã cập nhật odds nhanh hơn dữ liệu đang hiển thị trên giao diện.",
          "Cách hiển thị tên cược, market hoặc line có thể khác nhau giữa từng nhà cái.",
          "Tỷ lệ cược có thể khác nhau theo khu vực địa lý, loại tài khoản hoặc giới hạn riêng của từng người dùng.",
          "Kèo live biến động rất nhanh, đặc biệt khi trận đấu đang diễn ra.",
        ]} />

        <GuideSubTitle>Bạn nên kiểm tra gì?</GuideSubTitle>
        <GuideList type="check" items={[
          "Đúng trận đấu.",
          "Đúng nhà cái.",
          "Đúng tên cược.",
          "Đúng market.",
          "Đúng line.",
          "Đúng tỷ lệ cược hiện tại.",
        ]} />

        <GuideText>
          Nếu thông tin tại nhà cái đã khác với dữ liệu hiển thị trên phần mềm, bạn không nên đặt cược theo thông tin cũ. Hãy kiểm tra lại bằng công cụ tính phù hợp trước khi quyết định.
        </GuideText>

        <GuideSubTitle>Báo cáo lỗi</GuideSubTitle>
        <GuideText>
          Nếu bạn phát hiện cơ hội hiển thị sai, lệch line, lệch market, sai tên cược hoặc không khớp với nhà cái thực tế, vui lòng báo cáo cho chúng tôi. Chúng tôi sẽ kiểm tra và xử lý triệt để để cải thiện chất lượng dữ liệu.
        </GuideText>

        <GuideText>Cảm ơn bạn đã sử dụng phần mềm của chúng tôi.</GuideText>
      </GuideSection>
    </div>
  );
}

// ── Part 9: Sportsbook Settings ───────────────────────────────────────────────
function SportsbookSettings({ enabledSportsbooks, onEnabledSportsbooksChange }) {
  const totalCount = SELECTABLE_SPORTSBOOK_OPTIONS.length;
  const selectedCount = enabledSportsbooks.filter((id) => isSelectableSportsbookId(id)).length;
  const allSelected = selectedCount === totalCount;
  const noneSelected = selectedCount === 0;

  function toggle(id) {
    if (!isSelectableSportsbookId(id)) return;
    const next = enabledSportsbooks.includes(id)
      ? enabledSportsbooks.filter((x) => x !== id)
      : [...enabledSportsbooks, id];
    onEnabledSportsbooksChange(next);
  }

  function selectAll() {
    onEnabledSportsbooksChange([...SELECTABLE_SPORTSBOOK_IDS]);
  }

  function deselectAll() {
    onEnabledSportsbooksChange([]);
  }

  function selectGroup(groupItems) {
    const groupIds = groupItems.filter((s) => s.status === "active").map((s) => s.id);
    const next = [...new Set([...enabledSportsbooks, ...groupIds])];
    onEnabledSportsbooksChange(next);
  }

  function deselectGroup(groupItems) {
    const groupIds = new Set(groupItems.filter((s) => s.status === "active").map((s) => s.id));
    onEnabledSportsbooksChange(enabledSportsbooks.filter((id) => !groupIds.has(id)));
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, lineHeight: 1.55 }}>
        Chọn nhà cái muốn thấy trong các tab cơ hội.
      </div>

      {/* Summary row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: allSelected ? C.positive : noneSelected ? C.negative : C.accent }}>
          Đã chọn {selectedCount} / {totalCount} nhà cái khả dụng
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={selectAll}
            style={{
              fontSize: 11, fontWeight: 700, padding: "5px 10px",
              background: "rgba(76,111,255,0.12)", color: C.accent,
              border: "1px solid rgba(76,111,255,0.25)", borderRadius: 7, cursor: "pointer",
            }}
          >
            Chọn tất cả
          </button>
          <button
            onClick={deselectAll}
            style={{
              fontSize: 11, fontWeight: 700, padding: "5px 10px",
              background: "rgba(255,107,74,0.08)", color: C.negative,
              border: "1px solid rgba(255,107,74,0.22)", borderRadius: 7, cursor: "pointer",
            }}
          >
            Bỏ chọn tất cả
          </button>
        </div>
      </div>

      <Note>
        Danh mục hiện có 48 nhà cái; 2 nhà cái ngừng hoạt động không thể lựa chọn. Mã đối chiếu Sharp sẽ được chuẩn hóa sau khi có dữ liệu thật.
      </Note>

      {/* Grouped sportsbook rows */}
      <div style={{ marginTop: 14 }}>
        {SPORTSBOOK_GROUPS.map((group, gi) => {
          const activeGroupItems = group.items.filter((s) => s.status === "active");
          const groupSelectedCount = activeGroupItems.filter((s) => enabledSportsbooks.includes(s.id)).length;
          const groupTotal = activeGroupItems.length;
          return (
            <div
              key={group.key}
              style={{
                marginBottom: gi < SPORTSBOOK_GROUPS.length - 1 ? 18 : 0,
              }}
            >
              {/* Group header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
                paddingBottom: 6,
                borderBottom: "1px solid rgba(180,197,235,0.08)",
              }}>
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: C.dimmer,
                    letterSpacing: "0.8px", textTransform: "uppercase",
                  }}>
                    {group.title}
                  </span>
                  <span style={{
                    fontSize: 10,
                    color: groupSelectedCount === groupTotal ? C.positive : groupSelectedCount === 0 ? C.negative : C.accent,
                    fontWeight: 700,
                    marginLeft: 7,
                  }}>
                    {groupSelectedCount}/{groupTotal}
                  </span>
                  <div style={{ fontSize: 10, color: C.dimmer, opacity: 0.7, marginTop: 1 }}>{group.desc}</div>
                </div>
                <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                  <button
                    onClick={() => selectGroup(group.items)}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: "4px 8px",
                      background: "rgba(76,111,255,0.10)", color: C.accent,
                      border: "1px solid rgba(76,111,255,0.22)", borderRadius: 6, cursor: "pointer",
                    }}
                  >
                    Chọn nhóm
                  </button>
                  <button
                    onClick={() => deselectGroup(group.items)}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: "4px 8px",
                      background: "rgba(255,107,74,0.07)", color: C.negative,
                      border: "1px solid rgba(255,107,74,0.18)", borderRadius: 6, cursor: "pointer",
                    }}
                  >
                    Bỏ nhóm
                  </button>
                </div>
              </div>

              {/* Items */}
              <div>
                {group.items.map((sb) => {
                  const isInactive = sb.status !== "active";
                  const selected = !isInactive && enabledSportsbooks.includes(sb.id);
                  return (
                    <button
                      key={sb.id}
                      onClick={() => toggle(sb.id)}
                      disabled={isInactive}
                      aria-disabled={isInactive}
                      title={isInactive ? "Ngừng hoạt động" : undefined}
                      style={{
                        width: "100%",
                        background: isInactive
                          ? "rgba(255,255,255,0.015)"
                          : selected ? "rgba(76,111,255,0.10)" : "rgba(255,255,255,0.025)",
                        border: isInactive
                          ? "1px solid rgba(180,197,235,0.06)"
                          : selected ? "1px solid rgba(76,111,255,0.30)" : "1px solid rgba(180,197,235,0.08)",
                        borderRadius: 12,
                        padding: "9px 10px",
                        marginBottom: 7,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: isInactive ? "not-allowed" : "pointer",
                        opacity: isInactive ? 0.5 : 1,
                        textAlign: "left",
                        boxSizing: "border-box",
                      }}
                    >
                      {/* Logo badge */}
                      <div style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: selected ? "rgba(76,111,255,0.18)" : "rgba(255,255,255,0.06)",
                        border: selected ? "1px solid rgba(76,111,255,0.35)" : "1px solid rgba(180,197,235,0.10)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 900,
                        color: selected ? "#a8c0ff" : C.dimmer,
                        flexShrink: 0,
                        letterSpacing: "-0.3px",
                      }}>
                        {sb.logoText}
                      </div>

                      {/* Name + availability status */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: selected ? C.text : C.muted, lineHeight: 1.3 }}>
                            {sb.label}
                          </div>
                          {isInactive && (
                            <span style={{
                              fontSize: 9, fontWeight: 800, color: C.negative,
                              background: C.negativeBg, border: `1px solid ${C.negativeBorder}`,
                              borderRadius: 20, padding: "1px 6px", flexShrink: 0,
                            }}>
                              Ngừng hoạt động
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: C.dimmer, marginTop: 1 }}>
                          {isInactive ? "Ngừng hoạt động" : "Có thể lựa chọn"}
                        </div>
                      </div>

                      {/* Check indicator */}
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: selected ? C.accent : "transparent",
                        border: selected ? `1px solid ${C.accent}` : "1px solid rgba(180,197,235,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: 11,
                        color: "#fff",
                        fontWeight: 900,
                      }}>
                        {selected ? "✓" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {noneSelected && (
        <div style={{
          fontSize: 11, color: C.negative,
          background: "rgba(255,107,74,0.07)",
          border: "1px solid rgba(255,107,74,0.20)",
          borderRadius: 8, padding: "8px 10px", marginTop: 4, lineHeight: 1.55,
        }}>
          Nếu bỏ chọn tất cả, các tab cơ hội có thể không hiển thị dữ liệu khi bộ lọc được áp dụng.
        </div>
      )}

      <Note>
        Cài đặt này lưu trên thiết bị hiện tại. Sau khi áp dụng bộ lọc, các tab cơ hội sẽ chỉ ưu tiên hiển thị nhà cái bạn đã chọn.
      </Note>
    </div>
  );
}

// ── Option detail metadata ────────────────────────────────────────────────────
const OPTION_META = {
  value:       { title: "Máy tính Value Bet",             desc: "Tính EV nhanh từ odds đang nhập" },
  arb:         { title: "Máy tính Arbitrage",             desc: "Tính chia vốn và lợi nhuận chắc chắn" },
  odds:        { title: "Cài đặt hiển thị odds",          desc: "Chọn HK, Decimal hoặc American cho máy tính" },
  thresh:      { title: "Ngưỡng lọc EV / Arb / Middles", desc: "Cài đặt ngưỡng lọc bản nháp UI" },
  sportsbooks: { title: "Cài đặt nhà cái hiển thị",      desc: "Chọn nhà cái muốn thấy trong các cơ hội" },
  guide:       { title: "Hướng dẫn sử dụng",             desc: "Cách dùng từng tính năng và kiểm tra kèo trước khi cược" },
  telegram:    { title: "Cài đặt thông báo Telegram",    desc: "Liên kết Telegram và quản lý thông báo theo từng tab" },
  notice:      { title: "Lưu ý quan trọng",              desc: "Điều cần kiểm tra khi dùng dữ liệu odds" },
  about:       { title: "Giới thiệu về chúng tôi",       desc: "Sứ mệnh, dữ liệu và cách chúng tôi hỗ trợ người dùng" },
};

// ── In-page detail renderer ───────────────────────────────────────────────────
function OptionDetailContent({ section, optionsOddsFormat, onOptionsOddsFormatChange, enabledSportsbooks, onEnabledSportsbooksChange }) {
  switch (section) {
    case "value":       return <ValueBetCalc optionsOddsFormat={optionsOddsFormat} />;
    case "arb":         return <ArbCalc optionsOddsFormat={optionsOddsFormat} />;
    case "odds":        return <OddsSettings oddsFormat={optionsOddsFormat} onOddsFormatChange={onOptionsOddsFormatChange} />;
    case "thresh":      return <ThresholdSettings />;
    case "sportsbooks": return <SportsbookSettings enabledSportsbooks={enabledSportsbooks} onEnabledSportsbooksChange={onEnabledSportsbooksChange} />;
    case "guide":       return <UserGuide />;
    case "telegram":    return <TelegramNotificationSettings />;
    case "notice":      return <ImportantNotice />;
    case "about":       return <AboutUs />;
    default:            return null;
  }
}

// ── Option cards config ───────────────────────────────────────────────────────
const CARDS = [
  { key: "value",       icon: "💰", title: "Máy tính Value Bet",             desc: "Tính EV nhanh từ HK odds" },
  { key: "arb",         icon: "⚖️", title: "Máy tính Arbitrage",             desc: "Tính stake split & lợi nhuận chắc chắn" },
  { key: "odds",        icon: "🔢", title: "Cài đặt hiển thị odds",          desc: "HK / Decimal / American" },
  { key: "thresh",      icon: "🎚️", title: "Ngưỡng lọc EV / Arb / Middles", desc: "Bản nháp UI – chưa áp dụng thật" },
  { key: "sportsbooks", icon: "🏦", title: "Cài đặt nhà cái hiển thị",      desc: "Chọn nhà cái muốn thấy trong các cơ hội" },
  { key: "guide",       icon: "📘", title: "Hướng dẫn sử dụng",             desc: "Cách dùng từng tính năng và chọn kèo tốt" },
  { key: "telegram",    icon: "🔔", title: "Cài đặt thông báo Telegram",    desc: "Liên kết tài khoản và chọn thông báo cho 4 tab", fullWidth: true },
  { key: "notice",      icon: "⚠️", title: "Lưu ý quan trọng",              desc: "Điều cần kiểm tra khi dùng dữ liệu odds" },
  { key: "about",       icon: "👥", title: "Giới thiệu về chúng tôi",       desc: "Sứ mệnh, dữ liệu và cách chúng tôi hỗ trợ người dùng" },
];

// ── Main AnalysisStatsPage ────────────────────────────────────────────────────
export default function AnalysisStatsPage({ userPlan }) {
  const [activeOptionView, setActiveOptionView] = useState(null);
  const [optionsOddsFormat, setOptionsOddsFormat] = useState(getInitialOptionsOddsFormat);
  const [enabledSportsbooks, setEnabledSportsbooks] = useState(getInitialEnabledSportsbooks);

  const canManageTelegramSettings = userPlan === "premium";

  const openOptionView = useCallback(
    (key) => {
      if (key === "telegram" && !canManageTelegramSettings) return;
      setActiveOptionView(key);
    },
    [canManageTelegramSettings]
  );
  const closeOptionView = useCallback(() => setActiveOptionView(null), []);

  const handleOptionsOddsFormatChange = useCallback((next) => {
    const normalized = normalizeOptionsOddsFormat(next);
    setOptionsOddsFormat(normalized);
    saveOptionsOddsFormat(normalized);
  }, []);

  const handleEnabledSportsbooksChange = useCallback((nextIds) => {
    const normalized = saveEnabledSportsbooks(nextIds);
    setEnabledSportsbooks(normalized);
  }, []);

  return (
    <div style={{ minHeight: "100%", background: C.bg, padding: "20px 14px 84px", boxSizing: "border-box", overflowX: "hidden" }}>

      {activeOptionView === null ? (
        <>
          {/* Page header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>
              ⚙️ Tuỳ Chọn
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>Công cụ tính nhanh và cài đặt hiển thị</div>
          </div>

          {/* 2-column card grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
            width: "100%",
            maxWidth: 680,
            margin: "0 auto",
          }}>
            {CARDS.map((card) => {
              const telegramLocked = card.key === "telegram" && !canManageTelegramSettings;
              return (
                <button
                  key={card.key}
                  onClick={() => openOptionView(card.key)}
                  disabled={telegramLocked}
                  aria-disabled={telegramLocked}
                  style={{
                    background: "linear-gradient(180deg, rgba(17,31,66,0.98), rgba(13,26,56,0.98))",
                    border: "1px solid rgba(180,197,235,0.12)",
                    borderRadius: 16,
                    padding: "15px 13px",
                    minHeight: 108,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                    textAlign: "left",
                    cursor: telegramLocked ? "not-allowed" : "pointer",
                    opacity: telegramLocked ? 0.6 : 1,
                    transition: "background .18s, border-color .18s",
                    color: C.text,
                    width: "100%",
                    gridColumn: card.fullWidth ? "1 / -1" : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (telegramLocked) return;
                    e.currentTarget.style.background = "linear-gradient(180deg, rgba(26,44,90,0.98), rgba(17,31,66,0.98))";
                    e.currentTarget.style.borderColor = "rgba(76,111,255,0.30)";
                  }}
                  onMouseLeave={(e) => {
                    if (telegramLocked) return;
                    e.currentTarget.style.background = "linear-gradient(180deg, rgba(17,31,66,0.98), rgba(13,26,56,0.98))";
                    e.currentTarget.style.borderColor = "rgba(180,197,235,0.12)";
                  }}
                >
                  <div style={{ fontSize: 20, marginBottom: 7 }}>{card.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 3, lineHeight: 1.35 }}>
                    {card.title}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{card.desc}</div>
                  <div style={{ marginTop: 8, fontSize: 10, color: telegramLocked ? C.dimmer : C.accent, fontWeight: 600 }}>
                    {telegramLocked ? "🔒 Chỉ dành cho Premium" : "Mở →"}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* Detail screen */
        <div style={{ width: "100%", maxWidth: 380, margin: "0 auto" }}>
          {/* Back button row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <button
              onClick={closeOptionView}
              style={{
                background: "rgba(180,197,235,0.08)",
                border: "1px solid rgba(180,197,235,0.12)",
                borderRadius: 999,
                padding: "7px 11px",
                color: C.muted,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              ← Quay lại
            </button>
          </div>

          {/* Detail title */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 3 }}>
              {OPTION_META[activeOptionView]?.title}
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>
              {OPTION_META[activeOptionView]?.desc}
            </div>
          </div>

          {/* Detail card */}
          <div style={{
            background: "linear-gradient(180deg, rgba(13,26,56,0.98), rgba(9,16,34,0.98))",
            border: "1px solid rgba(180,197,235,0.12)",
            borderRadius: 16,
            padding: 14,
            boxShadow: "0 10px 28px rgba(0,0,0,0.24)",
          }}>
            <OptionDetailContent
              section={activeOptionView}
              optionsOddsFormat={optionsOddsFormat}
              onOptionsOddsFormatChange={handleOptionsOddsFormatChange}
              enabledSportsbooks={enabledSportsbooks}
              onEnabledSportsbooksChange={handleEnabledSportsbooksChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
